// A levelpack job (contracts/studio.md §5): progress, what it was asked to build, and the packs it drafted with
// links to review each one (or to the published pack once it is out).

import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import type { StudioJob, StudioJobPack } from "../../api/studioSchemas";
import { useCan } from "../../auth/AuthContext";
import { Button, Card, CategoryTag, Chip, EmptyState, PageHeader, Stat } from "../../components/ui";
import { formatDateTime } from "../../lib/dates";
import { fmtInt, typeSlug } from "../../lib/format";
import { isActiveJob, splitJobError, STUDIO_ERRORS } from "../../lib/studio";
import { Callout, JobStatusChip, KindChip, RoleNote } from "../studio/components";
import { CancelJob, jobDuration, JobProgress, LiveBadge } from "../studio/jobs";
import s from "../studio/studio.module.css";
import l from "./levelpacks.module.css";

const STATUS_TONE: Record<StudioJobPack["status"], string> = { draft: "accent", published: "success", deleted: "muted" };

export function LevelPackJob({ job, packs, back }: { job: StudioJob; packs: StudioJobPack[]; back: ReactNode }) {
  const navigate = useNavigate();
  const canEdit = useCan("studio.edit");
  const active = isActiveJob(job.status);
  const err = splitJobError(job.error);
  const known = err?.code ? STUDIO_ERRORS[err.code] : undefined;
  const track = job.params.track ?? job.type;
  const mix = job.params.typeMix;
  const curve = job.params.curve;
  return (
    <>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
            <CategoryTag category={track} />
            {job.counts.requested === 1 ? "Next pack" : `${job.counts.requested} packs`}
            <KindChip kind={job.kind} />
          </span>
        }
        meta={
          <>
            {back}
            <JobStatusChip status={job.status} cancelRequested={job.cancelRequested} />
            <span className="mono">{job.id}</span>
          </>
        }
        actions={
          <>
            <RoleNote />
            {active ? <LiveBadge live /> : null}
            <CancelJob job={job} size="md" />
            {canEdit ? (
              <Button variant="ghost" onClick={() => navigate(`/studio/new?kind=levelpack&track=${encodeURIComponent(track)}`)}>
                Generate another
              </Button>
            ) : null}
          </>
        }
      />
      <Card>
        <div className={s.jobStrip}>
          <Stat label="Packs requested" value={fmtInt(job.counts.requested)} sub="50 levels each" />
          <Stat label="Drafted" value={fmtInt(job.counts.created)} sub="solver-verified drafts" />
          <Stat label="Failed" value={fmtInt(job.counts.failedVerification)} sub="the job stops" />
          <Stat label="Type mix" value={mix ? "custom" : "schedule"} sub={mix ? Object.keys(mix).map(typeSlug).join(", ") : "the track's schedule"} />
          <Stat label="Difficulty" value={curve ? `${curve.start.toFixed(2)} → ${curve.end.toFixed(2)}` : "ramp"} sub={curve ? "straight line + saw" : "iqcontent's curve"} />
          <Stat label="Seed" value={job.params.seed ?? "canonical"} sub={job.params.seed ? "another take" : "same as the CLI"} />
        </div>
        <JobProgress job={job} />
        <div className={s.jobMeta}>
          <span>
            requested by <b>{job.requestedBy ?? "—"}</b>
          </span>
          <span>
            created <b>{formatDateTime(job.createdAt)}</b>
          </span>
          {job.finishedAt ? (
            <span>
              finished <b>{formatDateTime(job.finishedAt)}</b>
            </span>
          ) : null}
          <span>
            took <b className="mono">{jobDuration(job)}</b>
          </span>
        </div>
        {err ? (
          <div style={{ marginTop: 14 }}>
            <Callout tone="danger" title={known?.title ?? "The job failed"} code={err.code ?? undefined} testId="job-error">
              <p>{err.message}</p>
              {known?.hint ? <p className={s.calloutHint}>{known.hint}</p> : null}
            </Callout>
          </div>
        ) : null}
      </Card>

      <Card title="Packs" actions={<span className="monoLabel">{packs.length} built</span>}>
        {packs.length === 0 ? (
          <EmptyState title={active ? "Building…" : "No packs"}>
            {active ? "Each pack of 50 levels takes a few seconds on the engine; they appear here one by one." : "This job built no pack."}
          </EmptyState>
        ) : (
          <div className={l.packRows} data-testid="job-packs">
            {packs.map((p) => (
              <div key={p.index} className={l.packRow} data-status={p.status}>
                <strong>
                  Pack {p.index}
                  <span className="mono" style={{ marginLeft: 8, color: "var(--color-text-tertiary)", fontWeight: 400 }}>
                    L{p.first}–L{p.last}
                  </span>
                </strong>
                <span className={l.faint}>
                  {p.chapter ?? "—"}
                  {p.verified ? ` · ${p.verified.ok}/${p.verified.checked} solver-verified` : ""}
                  {p.sha256 ? ` · ${p.sha256.slice(0, 12)}…` : ""}
                </span>
                <Chip tone={STATUS_TONE[p.status]}>{p.status}</Chip>
                {p.status === "draft" ? (
                  <Link to={`/levels/${p.track}/${p.index}/draft`}>Review draft →</Link>
                ) : p.status === "published" ? (
                  <Link to={`/levels/${p.track}/${p.index}`}>Open pack →</Link>
                ) : (
                  <span className={l.faint}>deleted</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
